import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import toast from "react-hot-toast";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "12px",
};

const defaultCenter = {
  lat: -26.1958,
  lng: 28.1418,
};

const libraries = ["places"];

// Create a custom pin using Font Awesome icon style
const createFontAwesomePin = (google) => {
  if (!google) return null;

  const canvas = document.createElement("canvas");
  const size = 44;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  ctx.clearRect(0, 0, size, size);

  // Draw shadow
  ctx.shadowColor = "rgba(0, 0, 0, 0.3)";
  ctx.shadowBlur = 6;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 2;

  // Draw pin shape
  ctx.beginPath();
  ctx.arc(size / 2, size / 2 - 5, size / 3.5, 0, 2 * Math.PI);
  ctx.fillStyle = "#EA4335";
  ctx.fill();
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = 2;
  ctx.stroke();

  // Pin point
  ctx.beginPath();
  ctx.moveTo(size / 2 - 6, size / 2 + 3);
  ctx.lineTo(size / 2, size / 2 + 12);
  ctx.lineTo(size / 2 + 6, size / 2 + 3);
  ctx.fillStyle = "#EA4335";
  ctx.fill();

  // Inner circle
  ctx.beginPath();
  ctx.arc(size / 2, size / 2 - 5, size / 7, 0, 2 * Math.PI);
  ctx.fillStyle = "#FFFFFF";
  ctx.fill();

  ctx.shadowColor = "transparent";

  return {
    url: canvas.toDataURL(),
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size - 2),
    labelOrigin: new google.maps.Point(size / 2, size / 2 - 5),
  };
};

// Create cursor pin
const createCursorPin = (google) => {
  if (!google) return null;

  const canvas = document.createElement("canvas");
  const size = 32;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  ctx.clearRect(0, 0, size, size);

  ctx.beginPath();
  ctx.arc(size / 2, size / 2 - 4, size / 4, 0, 2 * Math.PI);
  ctx.fillStyle = "#EA4335";
  ctx.fill();
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(size / 2 - 5, size / 2 + 2);
  ctx.lineTo(size / 2, size / 2 + 9);
  ctx.lineTo(size / 2 + 5, size / 2 + 2);
  ctx.fillStyle = "#EA4335";
  ctx.fill();

  ctx.beginPath();
  ctx.arc(size / 2, size / 2 - 4, size / 8, 0, 2 * Math.PI);
  ctx.fillStyle = "#FFFFFF";
  ctx.fill();

  return {
    url: canvas.toDataURL(),
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size - 2),
  };
};

const AddressMapPicker = ({
  onAddressSelect,
  initialAddress,
  isOpen,
  onClose,
}) => {
  const [map, setMap] = useState(null);
  const [pinPosition, setPinPosition] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [isGeoLoading, setIsGeoLoading] = useState(false);
  const [zoom, setZoom] = useState(15);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [hasTriedCurrentLocation, setHasTriedCurrentLocation] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [isPinPlaced, setIsPinPlaced] = useState(false);
  const geocoder = useRef(null);
  const [customPin, setCustomPin] = useState(null);
  const [cursorPin, setCursorPin] = useState(null);

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (isLoaded && window.google) {
      setCustomPin(createFontAwesomePin(window.google));
      setCursorPin(createCursorPin(window.google));
    }
  }, [isLoaded]);

  useEffect(() => {
    if (isLoaded && window.google && window.google.maps) {
      try {
        geocoder.current = new window.google.maps.Geocoder();
        setMapError(null);
      } catch (error) {
        console.error("Failed to initialize geocoder:", error);
        setMapError("Failed to initialize maps service");
      }
    }
  }, [isLoaded]);

  useEffect(() => {
    if (isOpen && isLoaded && !hasTriedCurrentLocation && !pinPosition) {
      setHasTriedCurrentLocation(true);
      setTimeout(() => {
        getCurrentLocation();
      }, 500);
    }
  }, [isOpen, isLoaded, hasTriedCurrentLocation, pinPosition]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setIsGeoLoading(true);
    toast.loading("Getting your location...", { id: "location" });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const newPosition = { lat, lng };
        setPinPosition(newPosition);
        setIsPinPlaced(true);
        setShowInfoWindow(true);

        if (map) {
          map.panTo(newPosition);
          map.setZoom(17);
          setZoom(17);
        }

        reverseGeocode(newPosition);
        setIsGeoLoading(false);
        toast.success(
          "📍 Pin placed at your current location! Click anywhere to move it.",
          { id: "location" },
        );
      },
      (error) => {
        console.error("Geolocation error:", error);
        let errorMessage = "Could not get your current location. ";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += "Please allow location access and try again.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage += "Location request timed out.";
            break;
          default:
            errorMessage += "Please click on the map to select a location.";
        }
        toast.error(errorMessage, { id: "location" });
        setIsGeoLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  const geocodeAddress = useCallback(
    async (address) => {
      if (!geocoder.current) {
        toast.error("Map service not ready. Please try again.");
        return;
      }

      setIsGeoLoading(true);
      toast.loading("Searching for address...", { id: "geocode" });

      try {
        const response = await geocoder.current.geocode({ address });
        if (response.results && response.results[0]) {
          const location = response.results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();
          const newPosition = { lat, lng };
          setPinPosition(newPosition);
          setIsPinPlaced(true);
          setShowInfoWindow(true);

          if (map) {
            map.panTo(newPosition);
            map.setZoom(17);
            setZoom(17);
          }

          // Parse address components for the searched address
          const addressComponents = response.results[0].address_components;
          const formattedAddress = response.results[0].formatted_address;

          const addressData = {
            fullAddress: formattedAddress,
            streetNumber: "",
            route: "",
            locality: "",
            administrativeAreaLevel1: "",
            administrativeAreaLevel2: "",
            postalCode: "",
            country: "",
            addressLine1: "",
            city: "",
            state: "",
            lat: lat,
            lng: lng,
          };

          addressComponents.forEach((component) => {
            const types = component.types;
            const longName = component.long_name;

            if (types.includes("street_number")) {
              addressData.streetNumber = longName;
            }
            if (types.includes("route")) {
              addressData.route = longName;
            }
            if (types.includes("locality")) {
              addressData.locality = longName;
            }
            if (types.includes("administrative_area_level_1")) {
              addressData.administrativeAreaLevel1 = longName;
            }
            if (types.includes("administrative_area_level_2")) {
              addressData.administrativeAreaLevel2 = longName;
            }
            if (types.includes("postal_code")) {
              addressData.postalCode = longName;
            }
            if (types.includes("country")) {
              addressData.country = longName;
            }
          });

          // Build address line 1 with both street number and route
          let addressLine1Parts = [];
          if (addressData.streetNumber) {
            addressLine1Parts.push(addressData.streetNumber);
          }
          if (addressData.route) {
            addressLine1Parts.push(addressData.route);
          }

          let addressLine1 = addressLine1Parts.join(" ");

          // If no street number or route found, try to get the first line from formatted address
          if (!addressLine1.trim()) {
            const addressParts = formattedAddress.split(",");
            if (addressParts.length > 0) {
              addressLine1 = addressParts[0].trim();
            }
          }

          addressData.addressLine1 = addressLine1;
          addressData.city =
            addressData.locality || addressData.administrativeAreaLevel2 || "";
          addressData.state = addressData.administrativeAreaLevel1 || "";

          setSelectedAddress(formattedAddress);
          window.selectedAddressData = addressData;

          toast.success(
            "📍 Pin placed at the searched address! Click anywhere to move it.",
            { id: "geocode" },
          );
        } else {
          toast.error("Could not find this address on map", { id: "geocode" });
        }
      } catch (error) {
        console.error("Geocoding error:", error);
        toast.error(
          "Could not find this address. Please try selecting manually.",
          { id: "geocode" },
        );
      } finally {
        setIsGeoLoading(false);
      }
    },
    [map],
  );
  useEffect(() => {
    if (
      initialAddress &&
      geocoder.current &&
      isOpen &&
      isLoaded &&
      !hasTriedCurrentLocation &&
      !pinPosition
    ) {
      geocodeAddress(initialAddress);
    }
  }, [
    initialAddress,
    isOpen,
    isLoaded,
    geocodeAddress,
    hasTriedCurrentLocation,
    pinPosition,
  ]);

  // Click on map - place the pin at click location
  const onMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const newPosition = { lat, lng };
    setPinPosition(newPosition);
    setIsPinPlaced(true);
    reverseGeocode(newPosition);
    setShowInfoWindow(true);
    toast.success("📍 Pin moved! Getting address...", { duration: 1500 });
  }, []);

  const reverseGeocode = async (position) => {
    if (!geocoder.current) {
      toast.error("Map service not ready");
      return;
    }

    setIsGeoLoading(true);

    try {
      const response = await geocoder.current.geocode({ location: position });
      if (response.results && response.results[0]) {
        const address = response.results[0].formatted_address;
        setSelectedAddress(address);

        const addressComponents = response.results[0].address_components;
        const addressData = {
          fullAddress: address,
          streetNumber: "",
          route: "",
          locality: "",
          administrativeAreaLevel1: "",
          administrativeAreaLevel2: "",
          postalCode: "",
          country: "",
          addressLine1: "",
          city: "",
          state: "",
          lat: position.lat,
          lng: position.lng,
        };

        addressComponents.forEach((component) => {
          const types = component.types;
          const longName = component.long_name;
          const shortName = component.short_name;

          if (types.includes("street_number")) {
            addressData.streetNumber = longName;
          }
          if (types.includes("route")) {
            addressData.route = longName;
          }
          if (types.includes("locality")) {
            addressData.locality = longName;
          }
          if (types.includes("administrative_area_level_1")) {
            addressData.administrativeAreaLevel1 = longName;
          }
          if (types.includes("administrative_area_level_2")) {
            addressData.administrativeAreaLevel2 = longName;
          }
          if (types.includes("postal_code")) {
            addressData.postalCode = longName;
          }
          if (types.includes("country")) {
            addressData.country = longName;
          }
        });

        // Build address line 1 with both street number and route
        let addressLine1Parts = [];
        if (addressData.streetNumber) {
          addressLine1Parts.push(addressData.streetNumber);
        }
        if (addressData.route) {
          addressLine1Parts.push(addressData.route);
        }

        let addressLine1 = addressLine1Parts.join(" ");

        // If no street number or route found, try to get the first line from formatted address
        if (!addressLine1.trim()) {
          const addressParts = address.split(",");
          if (addressParts.length > 0) {
            addressLine1 = addressParts[0].trim();
          }
        }

        addressData.addressLine1 = addressLine1;
        addressData.city =
          addressData.locality || addressData.administrativeAreaLevel2 || "";
        addressData.state = addressData.administrativeAreaLevel1 || "";

        // Debug logging
        console.log("📝 Parsed Address Data:", {
          streetNumber: addressData.streetNumber,
          route: addressData.route,
          addressLine1: addressData.addressLine1,
          city: addressData.city,
          state: addressData.state,
          postalCode: addressData.postalCode,
          country: addressData.country,
        });

        setSelectedAddress(address);
        window.selectedAddressData = addressData;
      } else {
        toast.error(
          "Could not get address for this location. Try clicking a more specific area.",
        );
      }
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      toast.error("Could not get address for this location. Please try again.");
    } finally {
      setIsGeoLoading(false);
    }
  };

  const handleUseThisLocation = () => {
    if (window.selectedAddressData) {
      onAddressSelect(window.selectedAddressData);
      toast.success("✅ Address selected from map!");
      onClose();
    } else if (selectedAddress) {
      const basicAddressData = {
        fullAddress: selectedAddress,
        addressLine1: selectedAddress.split(",")[0],
        city: "",
        state: "",
        postalCode: "",
        country: "South Africa",
        lat: pinPosition?.lat,
        lng: pinPosition?.lng,
      };
      onAddressSelect(basicAddressData);
      toast.success("✅ Address selected from map!");
      onClose();
    } else {
      toast.error("No address selected. Please click on the map first.");
    }
  };

  const handleRetryCurrentLocation = () => {
    getCurrentLocation();
  };

  const handleZoomIn = () => {
    if (map) {
      const newZoom = zoom + 1;
      map.setZoom(newZoom);
      setZoom(newZoom);
    }
  };

  const handleZoomOut = () => {
    if (map) {
      const newZoom = zoom - 1;
      map.setZoom(newZoom);
      setZoom(newZoom);
    }
  };

  if (!isOpen) return null;

  if (!isLoaded) {
    return (
      <div className="map-modal-overlay" onClick={onClose}>
        <div className="map-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="map-modal-header">
            <h3>
              <i className="fa-regular fa-map"></i> Loading Map...
            </h3>
            <button className="map-modal-close" onClick={onClose}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
          <div className="map-loading">
            <div className="loading-spinner">
              <i className="fa-solid fa-circle-notch fa-spin fa-3x"></i>
              <p>Loading Google Maps...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadError || mapError) {
    return (
      <div className="map-modal-overlay" onClick={onClose}>
        <div className="map-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="map-modal-header">
            <h3>
              <i className="fa-regular fa-map"></i> Map Error
            </h3>
            <button className="map-modal-close" onClick={onClose}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
          <div
            className="map-loading"
            style={{ color: "#f44336", textAlign: "center", padding: "40px" }}
          >
            <i className="fa-regular fa-map fa-3x"></i>
            <p style={{ marginTop: "16px" }}>
              {loadError
                ? "Failed to load Google Maps. Please check your API key."
                : mapError}
            </p>
            <button
              className="cancel-btn"
              onClick={onClose}
              style={{ marginTop: "20px" }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="map-modal-overlay" onClick={onClose}>
      <div className="map-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="map-modal-header">
          <h3>
            <i className="fa-regular fa-map"></i> Select Location on Map
          </h3>
          <button className="map-modal-close" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div className="map-controls">
          <button
            className="map-control-btn current-location"
            onClick={handleRetryCurrentLocation}
            disabled={isGeoLoading}
          >
            {isGeoLoading ? (
              <i className="fa-solid fa-circle-notch fa-spin"></i>
            ) : (
              <i className="fa-solid fa-location-arrow"></i>
            )}
            <span>
              {isGeoLoading ? "Getting Location..." : "Use My Current Location"}
            </span>
          </button>
          <div className="zoom-controls">
            <button className="map-control-btn" onClick={handleZoomIn}>
              <i className="fa-solid fa-plus"></i>
            </button>
            <button className="map-control-btn" onClick={handleZoomOut}>
              <i className="fa-solid fa-minus"></i>
            </button>
          </div>
        </div>

        <div className="map-container">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={pinPosition || defaultCenter}
            zoom={zoom}
            onClick={onMapClick}
            onLoad={(map) => setMap(map)}
            options={{
              zoomControl: false,
              mapTypeControl: true,
              streetViewControl: true,
              fullscreenControl: true,
              clickableIcons: false,
              draggableCursor: cursorPin
                ? `url(${cursorPin.url}), crosshair`
                : "crosshair",
            }}
          >
            {isPinPlaced && pinPosition && (
              <Marker
                position={pinPosition}
                draggable={false}
                icon={customPin}
                title="Selected location - Click anywhere to move"
                animation={
                  window.google && window.google.maps.Animation
                    ? window.google.maps.Animation.DROP
                    : null
                }
              />
            )}

            {showInfoWindow && selectedAddress && isPinPlaced && (
              <InfoWindow
                position={pinPosition}
                onCloseClick={() => setShowInfoWindow(false)}
                options={{
                  pixelOffset: new window.google.maps.Size(0, -40),
                }}
              >
                <div className="map-info-window">
                  <strong>
                    <i className="fa-regular fa-map-pin"></i> Selected Location
                  </strong>
                  <p>{selectedAddress}</p>
                  <small
                    style={{
                      color: "#666",
                      fontSize: "11px",
                      display: "block",
                      marginTop: "8px",
                    }}
                  >
                    <i className="fa-solid fa-mouse-pointer"></i> Click anywhere
                    to move the pin
                  </small>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>

        <div className="map-modal-footer">
          <div className="selected-address-preview">
            <strong>
              <i className="fa-regular fa-map-pin"></i> Selected Address:
            </strong>
            <p>
              {isGeoLoading ? (
                <span>
                  <i className="fa-solid fa-circle-notch fa-spin"></i> Getting
                  address...
                </span>
              ) : (
                selectedAddress ||
                "📍 Click anywhere on the map to place the pin"
              )}
            </p>
          </div>
          <div className="map-modal-actions">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="confirm-btn"
              onClick={handleUseThisLocation}
              disabled={!selectedAddress || isGeoLoading}
            >
              <i className="fa-solid fa-check"></i> Use This Location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressMapPicker;
